/* eslint-disable @ngrx/no-store-subscription */
import {
    Address,
    Billing,
    BillingInfo,
    Documents,
    Flight,
    OfficialDocuments,
    RenterDetails,
    RenterDetailsAdditionalPilot,
    ReservationDetails,
} from '@/core/domain/billing/models/billing-body.model';
import { joinPhoneLada } from '@/common/utils/strings/phone.utils';
import { BillingResponse } from '@/core/domain/billing/models/billing-response.model';
import { BillingRepository } from '@/core/domain/billing/repositories/billing.repository';
import { updatePrecheckin } from '@/core/reservation/store/precheckin.actions';
import { selectPrecheckin } from '@/core/reservation/store/precheckin.selector';
import { selectUser } from '@/core/user/store/reservation.selector';
import { environment } from '@/env/environment';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of, switchMap, take } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class BillingImplRepository extends BillingRepository {
    constructor(
        private httpClient: HttpClient,
        private store: Store,
    ) {
        super();
    }

    override sendUserData(): Observable<BillingResponse> {
        const url = [
            environment.alamoApi.BASE_HOST,
            environment.alamoApi.ENDPOINTS.preCheckInSendData,
        ].join('/');

        return this.store.select(selectPrecheckin).pipe(
            take(1),
            switchMap((storageData) => {
                const payload: Billing =
                    this.mapStorageDataToBilling(storageData);

                return this.store.select(selectUser).pipe(
                    take(1),
                    switchMap((response) => {
                        const headers = new HttpHeaders().set(
                            'token',
                            `${response.token}`,
                        );

                        return this.httpClient.put<BillingResponse>(
                            url,
                            payload,
                            { headers },
                        );
                    }),
                );
            }),
        );
    }

    private mapStorageDataToBilling(storageData: any): Billing {
        // Map reservation details
        const reservationDetails: ReservationDetails = {
            pickupDate: storageData.pickupDate,
            returnDate: storageData.returnDate,
        };

        // Map renter details
        const renterDetails: RenterDetails = {
            name: storageData.name,
            lastName: storageData.lastName,
            email: storageData.email,
            phone: joinPhoneLada(storageData.lada, storageData.phone),
            birthDate: storageData.birthday,
            address: this.mapAddress(storageData),
            additionalPilot: storageData.additionalPilot
                ? this.mapAdditionalPilot(storageData)
                : undefined,
            flight: this.mapFlight(storageData),
        };

        // Map documents
        const documents: Documents = {
            renterDocuments: this.mapDocuments(storageData.renterDocuments),
            additionalPilot: storageData.additionalPilot
                ? this.mapDocuments(storageData.additionalPilot)
                : undefined,
        };

        // Map billing info
        const billingInfo: BillingInfo = {
            requiredBilling: storageData.requiredBilling,
            differentAddress: storageData.differentAddress,
            billing: storageData.billing,
            billingAddress: storageData.billingAddress,
        };

        return {
            reservationCode: storageData.code,
            reservationDetails,
            renterDetails,
            documents,
            billing: billingInfo,
        };
    }

    private mapAddress(storageData: any): Address {
        return {
            street: storageData.street,
            buildingNumber: storageData.outdoorNumber,
            countryId: storageData.countryId,
            countryName: storageData.country,
            countryCode: storageData.countryCode,
            stateId: storageData.stateId,
            stateName: storageData.state,
            cityId: storageData.cityId,
            cityName: storageData.city,
            suburbId: storageData.neighborhoodId,
            suburbName: storageData.neighborhood,
            zipCode: storageData.zipCode,
        };
    }

    private mapAdditionalPilot(
        additionalPilot: any,
    ): RenterDetailsAdditionalPilot {
        return {
            name: additionalPilot.name2,
            lastName: additionalPilot.lastName2,
            email: additionalPilot.email2,
            phone: additionalPilot.phone2,
        };
    }

    private mapFlight(storageData: any): Flight {
        return {
            airline: storageData.airlineName,
            flightNumber: storageData.flightNumber,
            returnAirline: storageData.airlineNameReturn,
            returnFlightNumber: storageData.flightNumberReturn,
        };
    }

    private mapDocuments(documents: any): OfficialDocuments {
        return {
            ID: {
                type: documents.ID.type,
                number: documents.ID.number,
                expirationDate: documents.ID.expirationDate,
            },
            driverLicense: {
                number: documents.driverLicense.number,
                countryCode: documents.driverLicense.countryCode,
                countryName: documents.driverLicense.countryName,
                state: documents.driverLicense.state,
                expirationDate: documents.driverLicense.expirationDate,
                issueDate: documents.driverLicense.issueDate,
            },
        };
    }

    override storeBilling(billingData: BillingInfo): Observable<boolean> {
        this.store.dispatch(
            updatePrecheckin({
                value: billingData,
            }),
        );
        return of(true);
    }
}
